import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { AuthProvider } from './contexts/AuthContext'
import { GOOGLE_CLIENT_ID } from './utils/config'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import DemoGenerator from './pages/DemoGenerator'
import EmailTemplates from './pages/EmailTemplates'
import SmsTemplates from './pages/SmsTemplates'
import Profile from './pages/Profile'
import Login from './pages/Login'
import Buildings from './pages/Buildings'
import BuildingDetail from './pages/BuildingDetail'
import CreateBuilding from './pages/CreateBuilding'
import BuildingMembers from './pages/BuildingMembers'
import BuildingUnits from './pages/BuildingUnits'
import BuildingJoinRequests from './pages/BuildingJoinRequests'
import Users from './pages/Users'
import UserDetail from './pages/UserDetail'
import CreateUser from './pages/CreateUser'
import UserBuildings from './pages/UserBuildings'
import UserRoles from './pages/UserRoles'
import ResetPassword from './pages/ResetPassword'
import EditEmailTemplate from './pages/EditEmailTemplate'
import EditSmsTemplate from './pages/EditSmsTemplate'
import StaticAssets from './pages/StaticAssets'
import StaticAssetDetail from './pages/StaticAssetDetail'
import UploadStaticAsset from './pages/UploadStaticAsset'
import Announcements from './pages/Announcements'
import AnnouncementDetail from './pages/AnnouncementDetail'
import CreateAnnouncement from './pages/CreateAnnouncement'
import AnnouncementStats from './pages/AnnouncementStats'
import AnnouncementRecipients from './pages/AnnouncementRecipients'
import Deliveries from './pages/Deliveries'
import DeliveryDetail from './pages/DeliveryDetail'
import CreateDelivery from './pages/CreateDelivery'
import EditDelivery from './pages/EditDelivery'
import DeliveryStats from './pages/DeliveryStats'
import Amenities from './pages/Amenities'
import AmenityDetail from './pages/AmenityDetail'
import CreateAmenity from './pages/CreateAmenity'
import Parking from './pages/Parking'
import ParkingSpotDetail from './pages/ParkingSpotDetail'
import CreateParkingSpot from './pages/CreateParkingSpot'
import BatchCreateParkingSpots from './pages/BatchCreateParkingSpots'
import FeatureFlags from './pages/FeatureFlags'
import CreateFeatureFlag from './pages/CreateFeatureFlag'
import FeatureTypeList from './pages/FeatureTypeList'
import FeatureTypeDetail from './pages/FeatureTypeDetail'
import LicensePlans from './pages/LicensePlans'
import CreateLicensePlan from './pages/CreateLicensePlan'
import EditLicensePlan from './pages/EditLicensePlan'
import BuildingLicenses from './pages/BuildingLicenses'
import LicenseCodes from './pages/LicenseCodes'
import SentryIssues from './pages/SentryIssues'
import SentryIssueDetail from './pages/SentryIssueDetail'
import Tasks from './pages/Tasks'
import CreateTask from './pages/CreateTask'
import TaskDetail from './pages/TaskDetail'
import TaskSettings from './pages/TaskSettings'
import DeviceInstallations from './pages/DeviceInstallations'
import AIModels from './pages/AIModels'
import AIUsage from './pages/AIUsage'
import KnowledgeBase from './pages/KnowledgeBase'
import KnowledgeDocuments from './pages/KnowledgeDocuments'
import KnowledgeDocumentUpload from './pages/KnowledgeDocumentUpload'
import KnowledgeDocumentDetail from './pages/KnowledgeDocumentDetail'
import KnowledgeTopics from './pages/KnowledgeTopics'
import KnowledgeChat from './pages/KnowledgeChat'
import KnowledgeSync from './pages/KnowledgeSync'
import NotificationEvents from './pages/NotificationEvents'
import EditPushTemplate from './pages/EditPushTemplate'
import { ErrorModal } from './components/common'
import { Toaster } from 'sonner'
import './App.css'

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <Toaster position="top-right" richColors closeButton />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />

            {/* Building-scoped routes (accessible to both admin and manager) */}
            <Route path="buildings" element={<Buildings />} />
            <Route path="buildings/:buildingId" element={<BuildingDetail />} />
            <Route path="buildings/:buildingId/members" element={<BuildingMembers />} />
            <Route path="buildings/:buildingId/units" element={<BuildingUnits />} />
            <Route path="buildings/:buildingId/join-requests" element={<BuildingJoinRequests />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="announcements/create" element={<CreateAnnouncement />} />
            <Route path="announcements/stats" element={<AnnouncementStats />} />
            <Route path="announcements/:announcementId" element={<AnnouncementDetail />} />
            <Route path="announcements/:announcementId/edit" element={<CreateAnnouncement />} />
            <Route path="announcements/:announcementId/recipients" element={<AnnouncementRecipients />} />
            <Route path="deliveries" element={<Deliveries />} />
            <Route path="deliveries/create" element={<CreateDelivery />} />
            <Route path="deliveries/stats" element={<DeliveryStats />} />
            <Route path="deliveries/:deliveryId" element={<DeliveryDetail />} />
            <Route path="deliveries/:deliveryId/edit" element={<EditDelivery />} />
            <Route path="amenities" element={<Amenities />} />
            <Route path="amenities/create" element={<CreateAmenity />} />
            <Route path="amenities/:amenityId" element={<AmenityDetail />} />
            <Route path="amenities/:amenityId/edit" element={<CreateAmenity />} />
            <Route path="parking" element={<Parking />} />
            <Route path="parking/:buildingId/create" element={<CreateParkingSpot />} />
            <Route path="parking/:buildingId/batch-create" element={<BatchCreateParkingSpots />} />
            <Route path="parking/:buildingId/spots/:parkingSpotId" element={<ParkingSpotDetail />} />
            <Route path="parking/:buildingId/spots/:parkingSpotId/edit" element={<CreateParkingSpot />} />
            <Route path="knowledge-base" element={<KnowledgeBase />} />
            <Route path="knowledge-base/documents" element={<KnowledgeDocuments />} />
            <Route path="knowledge-base/upload" element={<KnowledgeDocumentUpload />} />
            <Route path="knowledge-base/documents/:id" element={<KnowledgeDocumentDetail />} />
            <Route path="knowledge-base/topics" element={<KnowledgeTopics />} />
            <Route path="knowledge-base/chat" element={<KnowledgeChat />} />
            <Route path="knowledge-base/chat/:id" element={<KnowledgeChat />} />
            <Route path="knowledge-base/sync" element={<KnowledgeSync />} />
            <Route path="static-assets" element={<AdminRoute><StaticAssets /></AdminRoute>} />
            <Route path="static-assets/upload" element={<AdminRoute><UploadStaticAsset /></AdminRoute>} />
            <Route path="static-assets/:id" element={<AdminRoute><StaticAssetDetail /></AdminRoute>} />
            <Route path="profile" element={<Profile />} />

            {/* Admin-only routes */}
            <Route path="buildings/create" element={<AdminRoute><CreateBuilding /></AdminRoute>} />
            <Route path="demo" element={<AdminRoute><DemoGenerator /></AdminRoute>} />
            <Route path="notifications" element={<AdminRoute><NotificationEvents /></AdminRoute>} />
            <Route path="notifications/push/:templateType/edit" element={<AdminRoute><EditPushTemplate /></AdminRoute>} />
            <Route path="email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />
            <Route path="email-templates/new" element={<AdminRoute><EditEmailTemplate /></AdminRoute>} />
            <Route path="email-templates/edit/:templateType" element={<AdminRoute><EditEmailTemplate /></AdminRoute>} />
            <Route path="sms-templates" element={<AdminRoute><SmsTemplates /></AdminRoute>} />
            <Route path="sms-templates/new" element={<AdminRoute><EditSmsTemplate /></AdminRoute>} />
            <Route path="sms-templates/edit/:templateType" element={<AdminRoute><EditSmsTemplate /></AdminRoute>} />
            <Route path="license-plans" element={<AdminRoute><LicensePlans /></AdminRoute>} />
            <Route path="license-plans/create" element={<AdminRoute><CreateLicensePlan /></AdminRoute>} />
            <Route path="license-plans/:planId/edit" element={<AdminRoute><EditLicensePlan /></AdminRoute>} />
            <Route path="building-licenses" element={<AdminRoute><BuildingLicenses /></AdminRoute>} />
            <Route path="license-codes" element={<AdminRoute><LicenseCodes /></AdminRoute>} />
            <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
            <Route path="users/create" element={<AdminRoute><CreateUser /></AdminRoute>} />
            <Route path="users/:userId" element={<AdminRoute><UserDetail /></AdminRoute>} />
            <Route path="users/:userId/buildings" element={<AdminRoute><UserBuildings /></AdminRoute>} />
            <Route path="users/:userId/roles" element={<AdminRoute><UserRoles /></AdminRoute>} />
            <Route path="users/:userId/reset-password" element={<AdminRoute><ResetPassword /></AdminRoute>} />
            <Route path="feature-flags" element={<AdminRoute><FeatureFlags /></AdminRoute>} />
            <Route path="feature-flags/create" element={<AdminRoute><CreateFeatureFlag /></AdminRoute>} />
            <Route path="feature-flags/types" element={<AdminRoute><FeatureTypeList /></AdminRoute>} />
            <Route path="feature-flags/types/:featureType" element={<AdminRoute><FeatureTypeDetail /></AdminRoute>} />
            <Route path="sentry-issues" element={<AdminRoute><SentryIssues /></AdminRoute>} />
            <Route path="sentry-issues/:issueId" element={<AdminRoute><SentryIssueDetail /></AdminRoute>} />
            <Route path="tasks" element={<AdminRoute><Tasks /></AdminRoute>} />
            <Route path="tasks/settings" element={<AdminRoute><TaskSettings /></AdminRoute>} />
            <Route path="tasks/create" element={<AdminRoute><CreateTask /></AdminRoute>} />
            <Route path="tasks/:supportTaskId" element={<AdminRoute><TaskDetail /></AdminRoute>} />
            <Route path="tasks/:supportTaskId/edit" element={<AdminRoute><CreateTask /></AdminRoute>} />
            <Route path="device-installations" element={<AdminRoute><DeviceInstallations /></AdminRoute>} />
            <Route path="ai-models" element={<AdminRoute><AIModels /></AdminRoute>} />
            <Route path="ai-usage" element={<AdminRoute><AIUsage /></AdminRoute>} />
          </Route>
        </Routes>
        <ErrorModal />
      </Router>
    </AuthProvider>
    </GoogleOAuthProvider>
  )
}

export default App
